import { clamp, lerp, mulberry32 } from '../utils/math.js';

export const ORBIT_BEATS = 8;

function smoothstep(edge0, edge1, value) {
  const t = clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

/**
 * Seeded orbit variant: every play gets its own trajectory (radii, ellipse
 * tilt, figure-eight amount, optional 3-lobe wobble) and its own morph
 * schedule. Bounds keep the path inside the readable playfield ring.
 */
function makeVariant(rng) {
  return {
    baseR: 0.26 + rng() * 0.04,
    ellipse: 0.04 + rng() * 0.05,
    swap: rng() < 0.5,
    tilt: (rng() - 0.5) * Math.PI * 0.7,
    eightAmp: 0.55 + rng() * 0.45,
    wobbleAmp: rng() < 0.45 ? 0 : 0.018 + rng() * 0.022,
    wobblePhase: rng() * Math.PI * 2,
    schedule: {
      ellipseIn: 2 + rng() * 3,
      eightIn: 10 + rng() * 8,
      eightOut: 26 + rng() * 10
    }
  };
}

let VARIANT = makeVariant(mulberry32(1));

export function setOrbitVariant(seed) {
  VARIANT = makeVariant(mulberry32(seed >>> 0));
  return VARIANT;
}

/**
 * Smoothly evolves circle → ellipse → figure-eight → ellipse.
 * Transitions are derived from musical bars, so markers and playhead agree.
 */
export function orbitShapeAtTime(songTime, bpm) {
  const beat = 60 / bpm;
  const bar = Math.max(0, songTime / (beat * 4));
  const { schedule } = VARIANT;

  const intoEllipse = smoothstep(schedule.ellipseIn, schedule.ellipseIn + 2, bar);
  const intoEight = smoothstep(schedule.eightIn, schedule.eightIn + 4, bar);
  const outOfEight = smoothstep(schedule.eightOut, schedule.eightOut + 4, bar);

  const eight = intoEight * (1 - outOfEight) * VARIANT.eightAmp;
  const stretch = VARIANT.ellipse * intoEllipse;
  return {
    rx: VARIANT.baseR + (VARIANT.swap ? -stretch * 0.8 : stretch),
    ry: VARIANT.baseR + (VARIANT.swap ? stretch : -stretch * 0.8),
    eight
  };
}

/** Point on the orbit relative to the screen center, before tilt. */
function rawPoint(phase, shape) {
  const theta = phase * Math.PI * 2 - Math.PI / 2;
  let x = Math.cos(theta) * shape.rx;
  const circleY = Math.sin(theta) * shape.ry;
  const eightY = Math.sin(theta * 2) * shape.ry * 0.82;
  let y = lerp(circleY, eightY, shape.eight);

  if (VARIANT.wobbleAmp) {
    const wobble = 1 + VARIANT.wobbleAmp * Math.sin(theta * 3 + VARIANT.wobblePhase);
    x *= wobble;
    y *= wobble;
  }

  const cos = Math.cos(VARIANT.tilt);
  const sin = Math.sin(VARIANT.tilt);
  return { x: x * cos - y * sin, y: x * sin + y * cos };
}

export function phaseAtTime(songTime, bpm, direction = 1) {
  const beat = 60 / bpm;
  const cycles = songTime / (beat * ORBIT_BEATS);
  const normalized = ((cycles % 1) + 1) % 1;
  return direction < 0 ? 1 - normalized : normalized;
}

/**
 * @returns {{x:number,y:number,angle:number,phase:number}}
 */
export function pointAtPhase(phase, shape, direction = 1) {
  const point = rawPoint(phase, shape);
  const epsilon = 0.001 * (direction < 0 ? -1 : 1);
  const next = rawPoint(phase + epsilon, shape);

  return {
    x: 0.5 + point.x,
    y: 0.5 + point.y,
    angle: Math.atan2(next.y - point.y, next.x - point.x),
    phase
  };
}

export function pointAtTime(songTime, bpm, direction = 1) {
  const shape = orbitShapeAtTime(songTime, bpm);
  const phase = phaseAtTime(songTime, bpm, direction);
  return pointAtPhase(phase, shape, direction);
}

/** Samples the current orbit for drawing a stable guide. */
export function sampleOrbit(songTime, bpm, direction = 1, count = 96) {
  const shape = orbitShapeAtTime(songTime, bpm);
  const points = [];
  for (let i = 0; i <= count; i++) {
    points.push(pointAtPhase(i / count, shape, direction));
  }
  return points;
}
